import Service, { hours, minutes } from './Service';
import {
  normalizeResponse,
  object,
  arrayOf,
  string,
  boolean,
  number,
  rfc3339Timestamp,
} from '../utils/schema';
import { urlPath } from '../utils/utils';
import { restApiNewActivityCreated } from './TokenizedApiModelEntities';

const bankAccountSchema = object({
  id: string(),
  name: string(),
  account_number: string(),
  is_verified: boolean(),
  currency_code: string(),
  country_code: string(),
});

const transactionSchema = object({
  id: string(),
  account_id: string(),
  direction: string(),
  status: string(),
  quantity: number(true),
  fees: number(true),
  txid: string(),
  date_created: rfc3339Timestamp(),
  date_modified: rfc3339Timestamp(),
});

/** @category SDK Modules */
export default class Banking extends Service {
  /** @internal */
  constructor(tokenizedApi) {
    super(tokenizedApi);
  }

  linkAccountFieldsQuery = this.makeQuery(
    (query, countryCode, currencyCode) => ({
      ...query,
      staleTime: hours(1),
      enabled: query.enabled && !!countryCode && !!currencyCode,
    }),
    async (context, countryCode, currencyCode) => {
      if (!countryCode || !currencyCode) {
        return null;
      }

      const response = await this.tokenizedApi.endpoints.get(
        urlPath('banking', 'accountform', `${countryCode}-${currencyCode}`),
      );

      const { normalizedValue } = normalizeResponse(
        'Query link account form',
        response.data.data,
        object({
          fields: arrayOf(
            object({
              name: string(),
              label: string(),
              description: string(),
              type: string(),
            }),
          ),
        }),
      );
      return normalizedValue;
    },
  );

  accountsQuery = this.makeQuery(
    (query, profileId) => ({
      ...query,
      staleTime: minutes(10),
      enabled: query.enabled && !!profileId,
    }),
    async (context, profileId) => {
      if (!profileId) {
        return null;
      }

      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles', profileId, 'banking', 'accounts'),
      );

      const { normalizedValue } = normalizeResponse(
        'Query bank accounts',
        response.data.data,
        arrayOf(bankAccountSchema),
      );
      return normalizedValue;
    },
  );

  accountQuery = this.makeQuery(
    (query, profileId, id) => ({
      ...query,
      staleTime: minutes(10),
      enabled: query.enabled && !!profileId && !!id,
    }),
    async (context, profileId, id) => {
      if (!profileId || !id) {
        return null;
      }

      if (!id) {
        return null;
      }

      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles', profileId, 'banking', 'accounts', id),
      );

      const { normalizedValue } = normalizeResponse(
        'Query bank account',
        response.data.data,
        bankAccountSchema,
      );
      return normalizedValue;
    },
  );

  transfersQuery = this.makeQuery(
    (query, profileId) => ({
      ...query,
      staleTime: minutes(1),
      enabled: query.enabled && !!profileId,
    }),
    async (context, profileId) => {
      if (!profileId) {
        return null;
      }

      const response = await this.tokenizedApi.endpoints.get(
        urlPath('profiles', profileId, 'banking', 'transfers'),
      );

      const { normalizedValue } = normalizeResponse(
        'Query bank transfers',
        response.data.data,
        arrayOf(transactionSchema),
      );
      return normalizedValue;
    },
  );

  async linkAccount({
    profileId,
    name,
    countryCode,
    currencyCode,
    accountNumber,
    bankData,
  }) {
    let data = {
      name: name,
      account_number: accountNumber,
      currency_code: currencyCode,
      country_code: countryCode,
      bank_data: bankData,
    };
    let response = await this.tokenizedApi.endpoints.post(
      urlPath('profiles', profileId, 'banking', 'accounts'),
      data,
    );

    const { normalizedValue } = normalizeResponse(
      'Query bank account form',
      response.data?.data,
      object({
        id: string(),
      }),
    );

    this.accountsQuery.invalidate(profileId);

    return normalizedValue;
  }

  async deleteAccount(profileId, bankAccountId) {
    await this.tokenizedApi.endpoints.delete(
      urlPath('profiles', profileId, 'banking', 'accounts', bankAccountId),
    );

    this.accountsQuery.invalidate(profileId);
  }

  withdrawalsQuery = this.makeQuery(
    (query, profileId) => ({
      ...query,
      staleTime: minutes(10),
      enabled: query.enabled && !!profileId,
    }),
    async (context, profileId) => {
      if (!profileId) {
        return null;
      }
      throw new Error('NYI');
    },
  );

  async createWithdrawal({ profileId, sourceLockboxId, accountId, quantity }) {
    let data = {
      lock_box_id: sourceLockboxId,
      account_id: accountId,
      quantity: quantity,
    };
    let response = await this.tokenizedApi.endpoints.post(
      urlPath('profiles', profileId, 'banking', 'withdrawal'),
      data,
    );

    this.withdrawalsQuery.invalidate(profileId);

    const { normalizedValue } = normalizeResponse(
      'Create withdrawal',
      response.data.data,
      transactionSchema,
    );

    return normalizedValue;
  }

  async confirmWithdrawal({ profileId, withdrawalId, description }) {
    const { clientId, sourceName } =
      await this.tokenizedApi.getApplicationIdentifiers();

    let data = {
      handshake_client_id: clientId,
      handshake_source_name: sourceName,
      description: description,
      handshake_signing: true,
    };
    let response = await this.tokenizedApi.endpoints.put(
      urlPath('profiles', profileId, 'banking', 'withdrawal', withdrawalId),
      data,
    );

    this.withdrawalsQuery.invalidate(profileId);

    const { normalizedValue } = normalizeResponse(
      'Confirm withdrawal',
      response.data.data,
      restApiNewActivityCreated,
    );

    return normalizedValue;
  }

  beneficiaryFieldsQuery = this.makeQuery(
    (query, countryCode) => ({
      ...query,
      staleTime: hours(1),
      enabled: query.enabled && !!countryCode,
    }),
    async (context, countryCode) => {
      if (!countryCode) {
        return null;
      }

      const response = await this.tokenizedApi.endpoints.get(
        urlPath('banking', 'beneficiaryform', countryCode),
      );

      const { normalizedValue } = normalizeResponse(
        'Query beneficiary form',
        response.data.data,
        object({
          fields: arrayOf(
            object({
              name: string(),
              label: string(),
              value: string(),
              type: string(),
            }),
          ),
        }),
      );
      return normalizedValue;
    },
  );
}
